//@ts-nocheck
import { Box, LinearProgress, Typography } from '@mui/material';
import { CheckCircle, CircleDashed, CircleNotch } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import * as PropTypes from 'prop-types';
import './TripImportProgress.css';

const STEP_ORDER = [
  'reading',
  'extracting',
  'images',
  'finalizing',
  'creating'
];

const getReadingLabelKey = (sourceKind) => {
  switch (sourceKind) {
    case 'pdf':
      return 'tripImport.progress.readingPdf';
    case 'docx':
      return 'tripImport.progress.readingDocument';
    case 'url':
      return 'tripImport.progress.readingPage';
    default:
      return 'tripImport.progress.readingNotes';
  }
};

/**
 * Progress UI shown while the draft SSE stream is running, driven by the
 * latest `progress` event and the kind of source being read.
 *
 * @param {Object} props
 * @param {'text'|'pdf'|'docx'|'url'} [props.sourceKind]
 * @param {'reading'|'extracting'|'images'|'finalizing'|'creating'} [props.step]
 * @param {number} [props.dayNumber]
 * @param {string} [props.dayTitle]
 */
const TripImportProgress = ({ sourceKind, step, dayNumber, dayTitle }) => {
  const { t } = useTranslation(['trip']);
  const currentIndex = step ? Math.max(STEP_ORDER.indexOf(step), 0) : 0;

  const steps = [
    {
      key: 'reading',
      label: t(getReadingLabelKey(sourceKind))
    },
    {
      key: 'extracting',
      label:
        step === 'extracting' && dayNumber && dayTitle
          ? t('tripImport.progress.extractingWithDay', { dayNumber, dayTitle })
          : t('tripImport.progress.extracting')
    },
    {
      key: 'images',
      label: t('tripImport.progress.images')
    },
    {
      key: 'finalizing',
      label: t('tripImport.progress.finalizing')
    },
    {
      key: 'creating',
      label: t('tripImport.progress.creating')
    }
  ];

  return (
    <Box className='trip-import-progress' role='status' aria-live='polite'>
      {/* The "Agent is building your trip…" header now lives in the modal
          title (see CreateTrip.jsx) instead of being repeated here. */}
      <Box className='trip-import-progress-list'>
        {steps.map((stepInfo, index) => {
          const isDone = index < currentIndex;
          const isActive = index === currentIndex;
          const state = isDone ? 'done' : isActive ? 'active' : 'pending';

          return (
            <Box
              key={stepInfo.key}
              className={`trip-import-progress-step trip-import-progress-step--${state}`}
            >
              <Box className='trip-import-progress-step-icon'>
                {isDone ? (
                  <CheckCircle size={20} weight='fill' />
                ) : isActive ? (
                  <CircleNotch
                    size={20}
                    className='trip-import-progress-spin'
                  />
                ) : (
                  <CircleDashed size={20} />
                )}
              </Box>
              <Typography
                variant='body2'
                className='trip-import-progress-step-label'
              >
                {stepInfo.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
      <LinearProgress className='trip-import-progress-bar' />
    </Box>
  );
};

TripImportProgress.propTypes = {
  sourceKind: PropTypes.oneOf(['text', 'pdf', 'docx', 'url']),
  step: PropTypes.oneOf([
    'reading',
    'extracting',
    'images',
    'finalizing',
    'creating'
  ]),
  dayNumber: PropTypes.number,
  dayTitle: PropTypes.string
};

TripImportProgress.defaultProps = {
  sourceKind: 'text',
  step: 'reading',
  dayNumber: null,
  dayTitle: null
};

export default TripImportProgress;
